export namespace UserbotBatches {
  export type AccountBatchJobStatus =
    | "running"
    | "completed"
    | "failed"
    | "cancelled"
  export type AccountBatchItemStatus =
    | "pending"
    | "dispatched"
    | "succeeded"
    | "failed"
    | "skipped"
    | "cancelled"

  export type AccountBatchItemReason =
    | "client-not-eligible"
    | "launch-failed"
    | "cancelled"
    | "chain-failed"
    | "already-has-spare"
    | "no-active-primary"
    | "no-spare-slot"
    | "username-exhausted"
    | `dictionary-empty:${string}` // сервер может прислать и новые коды — отображение деградирует на raw

  export interface AccountBatchJob {
    id: string
    status: AccountBatchJobStatus
    templateId: string | null
    bodySnapshot?: { name?: string; [key: string]: unknown } | null
    targetSnapshot: {
      clientIds?: string[]
      clientId?: string
      role?: string
      all?: boolean
      roleMode?: "one" | "all"
      targetUnit?: "client" | "account"
      targetFilter?: string
    }
    params: Record<string, unknown>
    staggerMs: number
    createdBy: string | null
    counters: {
      total: number
      dispatched: number
      succeeded: number
      failed: number
      skipped: number
      cancelled: number
    }
    createdAt: string
    updatedAt: string
  }

  export interface AccountBatchItem {
    id: string
    jobId: string
    clientId: string
    accountId: string | null
    chainId: string | null
    deliveryState: "reserved" | "started" | "reported" | null
    position?: number
    scheduleDate?: string | null
    attempt: number
    status: AccountBatchItemStatus
    reason: string | null
    failedReason: string | null
    createdAt: string
    updatedAt: string
  }

  export type AccountBatchTarget =
    | { clientIds: string[] }
    | { role: string }
    | { all: true }

  export interface CreateAccountBatchDto {
    templateId: string
    target: AccountBatchTarget
    params?: Record<string, unknown>
    staggerMs?: number
    dryRun?: boolean
  }

  export interface AccountBatchDryRunItem {
    clientId: string
    accountId: string | null
    preCheck:
      | "ok"
      | "client-not-eligible"
      | "already-has-spare"
      | "no-active-primary"
  }
  export interface AccountBatchDryRunRes {
    dryRun: true
    templateId: string
    counters: { total: number; wouldRun: number; skipped: number }
    items: AccountBatchDryRunItem[]
  }
  export type CreateAccountBatchRes = AccountBatchJob | AccountBatchDryRunRes
  export const isDryRunRes = (
    r: CreateAccountBatchRes,
  ): r is AccountBatchDryRunRes => (r as AccountBatchDryRunRes).dryRun === true

  /**
   * Продовый бэкенд отдаёт счётчики плоско на объекте задачи, а не внутри counters.
   * Нормализатор принимает и плоскую, и каноническую вложенную форму.
   */
  export const normalizeAccountBatchJob = (
    raw: AccountBatchJob,
  ): AccountBatchJob => {
    if (raw.counters) return raw
    const {
      total = 0,
      dispatched = 0,
      succeeded = 0,
      failed = 0,
      skipped = 0,
      cancelled = 0,
    } = raw as AccountBatchJob & Record<string, number | undefined>
    return {
      ...raw,
      counters: { total, dispatched, succeeded, failed, skipped, cancelled },
    }
  }

  /** Missing legacy admission is ambiguous, never proof of non-delivery. */
  export const normalizeAccountBatchItem = (
    raw: AccountBatchItem,
  ): AccountBatchItem => ({ ...raw, deliveryState: raw.deliveryState ?? null })

  /**
   * Нормализатор ответа создания батча: обрабатывает как dryRun, так и обычный запуск.
   * Если dryRun === true: гарантирует наличие counters (при отсутствии собирает из плоских total/wouldRun/skipped).
   * Иначе делегирует в normalizeAccountBatchJob.
   */
  export const normalizeCreateAccountBatchRes = (
    res: CreateAccountBatchRes,
  ): CreateAccountBatchRes => {
    if (isDryRunRes(res)) {
      if (res.counters) return res
      const {
        total = 0,
        wouldRun = 0,
        skipped = 0,
      } = res as AccountBatchDryRunRes & Record<string, number | undefined>
      return {
        ...res,
        counters: { total, wouldRun, skipped },
      }
    }
    return normalizeAccountBatchJob(res)
  }

  export interface AccountBatchDetail extends AccountBatchJob {
    items: AccountBatchItem[]
    itemsTotal: number
  }
  export interface RetryAccountBatchRes {
    job: AccountBatchJob
    replayed: number
  }

  export const PROFILE_DICT_KINDS = [
    "first_name_male",
    "first_name_female",
    "last_name",
    "username",
    "username_prefix",
    "username_suffix",
    "avatar_male",
    "avatar_female",
    "avatar_abstract",
  ] as const
  export type ProfileDictKind = (typeof PROFILE_DICT_KINDS)[number]

  export interface ProfileDictItem {
    id: string
    kind: ProfileDictKind
    value: string
    createdAt: string
    updatedAt: string
  }
  export interface ImportProfileDictRes {
    imported: number
    skipped: number
  }

  /* --- Аддитивные query/DTO для эндпоинтов (контракты дельт §1.1–§1.3) --- */
  export interface AccountBatchesQueryParams {
    limit?: number
    offset?: number
  }
  export interface AccountBatchDetailQueryParams {
    status?: AccountBatchItemStatus
    limit?: number
    offset?: number
  }
  export interface ProfileDictionariesQueryParams {
    kind?: ProfileDictKind
    limit?: number
    offset?: number
  }
  export interface CreateProfileDictDto {
    kind: ProfileDictKind
    value: string
  }
  export interface ImportProfileDictDto {
    kind: ProfileDictKind
    values: string[]
  }
  export interface DeleteProfileDictRes {
    deleted: true
  }
  /** Пустой массив = «без ролей»; не задано = каскад от freshest living sibling. */
  export interface PromoteClientDto {
    roleNames?: string[]
  }
}
